/**
 * Week-axis helpers for the 13-week scorecard grid.
 *
 * "Week-of" is the Monday at UTC midnight. Pinning to UTC keeps the
 * grid stable across timezones — a user in Pacific time and a user in
 * Eastern time looking at the same metric agree which week they're
 * editing. The server validates incoming weekOf values to be Mondays
 * via the same logic.
 */

/** Returns the Monday (UTC) of the week containing the given date. */
export function mondayOfWeek(date: Date): Date {
  // ISO weeks: Monday = 1, Sunday = 0 in JS Date.getUTCDay().
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = d.getUTCDay();
  // shift back by however many days we are past Monday; Sunday (0)
  // shifts back 6 to land on the prior Monday.
  const offset = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + offset);
  return d;
}

/**
 * Returns the most recent N Mondays inclusive of the current week,
 * sorted DESCENDING (current week first). N defaults to 13 to match
 * the EOS L10 13-week-rolling convention.
 */
export function rollingMondays(now: Date = new Date(), count = 13): Date[] {
  const start = mondayOfWeek(now);
  const out: Date[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(start);
    d.setUTCDate(start.getUTCDate() - i * 7);
    out.push(d);
  }
  return out;
}

/** Format a UTC Monday as YYYY-MM-DD for the server contract. */
export function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Short label for the grid header — e.g. "Apr 27". */
export function shortMonthDay(date: Date): string {
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}
// ── Local calendar days (ADR-048) ─────────────────────────────────────────
//
// The weekly grid above is anchored to UTC-midnight Mondays. A DAY, by
// contrast, is a local calendar date — a warehouse's "today" is an ET date,
// not a UTC instant. These helpers keep that distinction explicit, because
// "which midnight?" is the entire failure mode of the daily grain.

/** The org's default reporting zone. Every cron in the registry is ET-anchored. */
export const DEFAULT_REPORTING_TIME_ZONE = 'America/New_York';

/**
 * Format an instant as the YYYY-MM-DD calendar date OBSERVED in `timeZone`.
 *
 * Uses Intl rather than `getDate()` (which reads the server's zone) or 24h
 * subtraction (which drops a date in spring and repeats one in autumn).
 */
export function isoDateInZone(date: Date, timeZone: string = DEFAULT_REPORTING_TIME_ZONE): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const y = parts.find((p) => p.type === 'year')?.value ?? '0000';
  const m = parts.find((p) => p.type === 'month')?.value ?? '00';
  const d = parts.find((p) => p.type === 'day')?.value ?? '00';
  return `${y}-${m}-${d}`;
}

/** Today's calendar date in `timeZone`, as YYYY-MM-DD. */
export function todayInZone(timeZone: string = DEFAULT_REPORTING_TIME_ZONE, now: Date = new Date()): string {
  return isoDateInZone(now, timeZone);
}

/**
 * A YYYY-MM-DD calendar date as the UTC-midnight Date that Postgres `DATE`
 * columns hydrate to. Throws on a malformed string rather than silently
 * producing an Invalid Date that would land in the database.
 */
export function localDateToUtcMidnight(iso: string): Date {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    throw new Error(`localDate must be YYYY-MM-DD, got: ${iso}`);
  }
  const d = new Date(`${iso}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) throw new Error(`localDate is not a real date: ${iso}`);
  // Round-trip guard: '2026-02-30' parses in some engines but is not that day.
  if (toIsoDate(d) !== iso) throw new Error(`localDate is not a real date: ${iso}`);
  return d;
}

/** The Monday (UTC midnight) of the week containing a YYYY-MM-DD local date. */
export function weekOfLocalDate(iso: string): Date {
  return mondayOfWeek(localDateToUtcMidnight(iso));
}

/** ISO weekday, 1 = Monday .. 7 = Sunday, for a YYYY-MM-DD local date. */
export function isoWeekday(iso: string): number {
  const day = localDateToUtcMidnight(iso).getUTCDay();
  return day === 0 ? 7 : day;
}

/** Is this local date one the goal applies on? */
export function isGoalWeekday(iso: string, goalWeekdays: readonly number[]): boolean {
  return goalWeekdays.includes(isoWeekday(iso));
}

/**
 * The `count` calendar dates ending at `endIso` inclusive, oldest first.
 * Calendar arithmetic on UTC-midnight dates, so DST never skips or repeats a
 * date the way subtracting 86_400_000 would.
 */
export function localDatesEndingAt(endIso: string, count: number): string[] {
  const end = localDateToUtcMidnight(endIso);
  const out: string[] = [];
  for (let i = count - 1; i >= 0; i--) {
    const d = new Date(end);
    d.setUTCDate(end.getUTCDate() - i);
    out.push(toIsoDate(d));
  }
  return out;
}

/** Every calendar date from `startIso` to `endIso` inclusive, oldest first. */
export function localDatesBetween(startIso: string, endIso: string): string[] {
  const start = localDateToUtcMidnight(startIso);
  const end = localDateToUtcMidnight(endIso);
  const out: string[] = [];
  for (const d = new Date(start); d.valueOf() <= end.valueOf(); d.setUTCDate(d.getUTCDate() + 1)) {
    out.push(toIsoDate(d));
  }
  return out;
}
